import { FC, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DataGridPro, GridColDef, GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { useTranslation } from 'react-i18next';
import { gettingBonusAndDeduction } from 'utils/BonusAndDeduction';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ReplayIcon from '@mui/icons-material/Replay';
import { Divider, ListItemIcon, ListItemText, Menu, MenuItem, Typography, Chip, Avatar, Tooltip } from '@mui/material';
import { lazy, Suspense } from 'react';
import { BonusRule } from 'repository/timekeeper/@types/BonusRuleModel';
import { buildSettingsBasePath, buildSettingsCrudPath, parseSettingsCrudRoute } from 'utils/settingsCrudRouting';

const Add = lazy(() => import('components/dialog/bonus_and_deduction_rule/Add'));
const Edit = lazy(() => import('components/dialog/bonus_and_deduction_rule/Edit'));
const Delete = lazy(() => import('components/dialog/bonus_and_deduction_rule/Delete'));

/* interface RowData {
    id: number;
    name: string;
    amount: string;
    paycode: string;
    break_rule: string;
    activate_by: string;
    description: string;
} */

interface ContextMenuPosition {
    mouseX: number;
    mouseY: number;
}

interface ContextMenuProps {
    t: any;
    contextMenu: ContextMenuPosition | null;
    setContextMenu: (menu: ContextMenuPosition | null) => void;
    selectedRow: BonusRule[];
    reloading: () => void;
    rows: BonusRule[];
    clearSelection: () => void;
}

interface MenuOption {
    name: string;
    icon: React.ReactElement;
    click: () => void;
    color: string;
    divider: boolean;
    disabled: boolean;
    selected: boolean;
    onlyOne: boolean;
    permision: boolean;
}

const ContextMenu: FC<ContextMenuProps> = ({ t, contextMenu, setContextMenu, selectedRow, reloading, rows, clearSelection }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [openDelete, setOpenDelete] = useState<boolean>(false);
    const basePath = buildSettingsBasePath('bonus_and_deduction_rules');
    const modalRoute = parseSettingsCrudRoute(location.pathname, 'bonus_and_deduction_rules');
    const openAdd = modalRoute.action === 'add';
    const openEdit = (modalRoute.action === 'update' || modalRoute.action === 'detail') && modalRoute.id !== null;
    const selectedRowFromRoute = modalRoute.id === null ? null : rows.find((row) => Number(row.id) === modalRoute.id);
    const selectedRowsForRoute = selectedRowFromRoute ? [selectedRowFromRoute] : selectedRow;

    const closeRouteModal = () => {
        navigate(basePath);
    };

    const options: MenuOption[] = [
        {
            name: t('settings.bonus_and_deduction_rule.table.context_menu.add'),
            icon: <AddIcon />,
            click: () => {
                navigate(buildSettingsCrudPath('bonus_and_deduction_rules', 'add'));
                setContextMenu(null);
            },
            color: '',
            divider: false,
            disabled: false,
            selected: false,
            onlyOne: false,
            permision: JSON.parse(localStorage.getItem('permissions') || '[]').find((permision: string) => permision === 'timekeeper.BonusRule') ? true : false,
        },
        {
            name: t('settings.bonus_and_deduction_rule.table.context_menu.edit'),
            icon: <EditIcon />,
            click: () => {
                const current = selectedRow[0];
                if (!current?.id) return;
                navigate(buildSettingsCrudPath('bonus_and_deduction_rules', 'update', current.id));
                setContextMenu(null);
            },
            color: '',
            divider: false,
            disabled: selectedRow.length !== 1 ? true : false,
            selected: false,
            onlyOne: true,
            permision: JSON.parse(localStorage.getItem('permissions') || '[]').find((permision: string) => permision === 'timekeeper.UpdateBonusRule') ? true : false,
        },
        {
            name: t('settings.bonus_and_deduction_rule.table.context_menu.delete'),
            icon: <DeleteIcon color="error" />,
            click: () => {
                setOpenDelete(true);
                setContextMenu(null);
            },
            color: 'error',
            divider: false,
            disabled: true,
            selected: false,
            onlyOne: true,
            permision: JSON.parse(localStorage.getItem('permissions') || '[]').find((permision: string) => permision === 'timekeeper.DeleteBonusRule') ? true : false,
        },
        {
            name: t('settings.bonus_and_deduction_rule.table.context_menu.reload'),
            icon: <ReplayIcon />,
            click: () => {
                reloading();
                setContextMenu(null);
            },
            color: '',
            divider: true,
            disabled: false,
            selected: false,
            onlyOne: false,
            permision: true,
        },
    ];

    return (
        <>
            <Menu
                open={contextMenu ? true : false}
                onClose={() => setContextMenu(null)}
                anchorReference="anchorPosition"
                anchorPosition={contextMenu ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}
                sx={{ boxShadow: 'none' }}
            >
                {options
                    .filter((option) => option.permision)
                    .map((option, index) => (
                        <div key={index}>
                            {option.divider ? <Divider /> : null}

                            <MenuItem onClick={option.click} disabled={option.disabled}>
                                <ListItemIcon>{option.icon}</ListItemIcon>

                                <ListItemText primary={option.name} slotProps={{ primary: { color: option.color } }} />

                                {option.selected ? (
                                    <Typography sx={{ pl: 3 }} variant="body2" color={option.color}>
                                        {t('settings.paycodes.table.context_menu.selected')}:{selectedRow.length}
                                    </Typography>
                                ) : null}

                                {option.onlyOne ? (
                                    <Typography sx={{ pl: 3 }} variant="body2" color="text.secondary">
                                        {t('settings.paycodes.table.context_menu.only_one')}
                                    </Typography>
                                ) : null}
                            </MenuItem>
                        </div>
                    ))}
            </Menu>

            {openAdd && (
                <Suspense fallback={<div>Cargando...</div>}>
                    <Add open={openAdd} setOpen={(nextOpen: boolean) => { if (!nextOpen) closeRouteModal(); }} reloading={reloading} rows={rows} />
                </Suspense>
            )}
            {openEdit && <Edit open={openEdit} setOpen={(nextOpen: boolean) => { if (!nextOpen) closeRouteModal(); }} selectedRow={selectedRowsForRoute} reloading={reloading} rows={rows} clearSelection={clearSelection} />}
            {openDelete && <Delete open={openDelete} setOpen={setOpenDelete} selectedRow={selectedRow} reloading={reloading} clearSelection={clearSelection} />}
        </>
    );
};

const BonusAndDeductionTable: FC = () => {
    const { t } = useTranslation();
    const [contextMenu, setContextMenu] = useState<ContextMenuPosition | null>(null);
    const [selectedRow, setSelectedRow] = useState<BonusRule[]>([]);
    const [selected, setSelected] = useState<BonusRule[]>([]);
    const [loadingBonus, setLoadingBonus] = useState<boolean>(true);
    const [rows, setRows] = useState<BonusRule[]>([]);

    const activate_by: Record<string, string> = {
        hour_day: t('settings.bonus_and_deduction_rule.table.modals.add.hour_day'),
        shift_duration: t('settings.bonus_and_deduction_rule.table.modals.add.shift_duration'),
        paycode: t('settings.bonus_and_deduction_rule.table.modals.add.paycode'),
    };

    const columns: GridColDef[] = [
        {
            field: 'name',
            headerName: t('settings.bonus_and_deduction_rule.table.columns.name'),
            flex: 1,
        },
        {
            field: 'amount',
            headerName: t('settings.bonus_and_deduction_rule.table.columns.amount'),
            flex: 1,
            align: 'center',
        },
        {
            field: 'paycode',
            headerName: t('settings.bonus_and_deduction_rule.table.columns.paycode'),
            flex: 1,
            renderCell: (params) => {
                const name = params.value?.name ?? 'N/A';
                return <Chip avatar={<Avatar>{name.charAt(0)}</Avatar>} label={name} />;
            },
        },
        {
            field: 'break_rule',
            headerName: t('settings.bonus_and_deduction_rule.table.columns.cancel_if_a_break_is_taken'),
            flex: 1,
            renderCell: (params) => {
                const name = params.value?.name ?? 'N/A';
                return (
                    <Chip
                        style={{ cursor: 'pointer' }}
                        avatar={
                            <Tooltip title={t('settings.submenu.break_rules')}>
                                <Avatar>{name.charAt(0)}</Avatar>
                            </Tooltip>
                        }
                        label={name}
                    />
                );
            },
        },
        {
            field: 'activate_by',
            headerName: t('settings.bonus_and_deduction_rule.table.columns.activate_by'),
            flex: 1,
            renderCell: (params) => {
                return <Typography variant="body2">{activate_by[params.value as string]}</Typography>;
            },
        },
    ];

    const reloading = () => {
        if (localStorage.getItem('access_token') && JSON.parse(localStorage.getItem('permissions') || '[]').find((permision: string) => permision === 'timekeeper.BonusRule')) {
            gettingBonusAndDeduction(setLoadingBonus, (data: BonusRule[]) => {
                // Ordenar alfabeticamente por name antes de setear.
                const sortedData = [...data].sort((a, b) => String(a?.name ?? '').localeCompare(String(b?.name ?? ''), 'es', { sensitivity: 'base' }));
                setRows(sortedData);
            });
        }
    };

    const clearSelection = () => {
        setSelectedRow([]);
        setSelected([]);
    };

    useEffect(() => {
        reloading();
    }, []);

    return (
        <>
            <div
                style={{ width: '100%', height: '100%' }}
                onContextMenu={(event) => {
                    event.preventDefault();
                    setContextMenu(contextMenu === null ? { mouseX: event.clientX + 2, mouseY: event.clientY - 6 } : null);
                }}
            >
                <DataGridPro
                    sx={{ height: 'calc(100vh - 140px)' }}
                    columns={columns}
                    rows={rows}
                    initialState={{
                        sorting: {
                            sortModel: [{ field: 'name', sort: 'asc' }],
                        },
                    }}
                    showCellVerticalBorder
                    showColumnVerticalBorder
                    headerFilters
                    localeText={{
                        headerFilterOperatorContains: t('general.table.headerFilters.headerFilterOperatorContains'),
                        headerFilterOperatorEquals: t('general.table.headerFilters.headerFilterOperatorEquals'),
                        headerFilterOperatorStartsWith: t('general.table.headerFilters.headerFilterOperatorStartsWith'),
                        headerFilterOperatorEndsWith: t('general.table.headerFilters.headerFilterOperatorEndsWith'),
                        headerFilterOperatorIsEmpty: t('general.table.headerFilters.headerFilterOperatorIsEmpty'),
                        headerFilterOperatorIsNotEmpty: t('general.table.headerFilters.headerFilterOperatorIsNotEmpty'),
                        footerRowSelected: (count: number) =>
                            count > 1 ? t('general.table.footerFilters.footerRowSelected.>1', { count: count }) : t('general.table.footerFilters.footerRowSelected.=1', { count: count }),

                        footerTotalRows: t('general.table.footerFilters.footerTotalRows'),
                        MuiTablePagination: {
                            labelDisplayedRows: ({ from, to, count }: { from: number; to: number; count: number }) =>
                                t('general.table.footerFilters.labelDisplayedRows', { from: from, to: to, count: count }),
                            labelRowsPerPage: t('general.table.footerFilters.labelRowsPerPage'),
                        },
                    }}
                    disableColumnMenu
                    loading={loadingBonus}
                    checkboxSelection
                    onRowSelectionModelChange={(newRowSelectionModel: GridRowSelectionModel) => {
                        const row = rows.filter((row) => newRowSelectionModel.includes(row.id));
                        setSelectedRow(newRowSelectionModel as number[]);
                        setSelected(row);
                    }}
                    rowSelectionModel={selectedRow}
                    pagination
                />

                <ContextMenu t={t} contextMenu={contextMenu} setContextMenu={setContextMenu} selectedRow={selected} reloading={reloading} rows={rows} clearSelection={clearSelection} />
            </div>
        </>
    );
};

export default BonusAndDeductionTable;
